import React, { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { FaCheckCircle, FaAward, FaUsers, FaGlobe, FaStar, FaTimes, FaRocket, FaHeart, FaShieldAlt, FaLightbulb, FaHandshake, FaMapMarkerAlt, FaEnvelope, FaCode, FaMobile, FaChartLine } from "react-icons/fa";
import { Link } from "react-scroll";

const features = [
  "2 Year of Industry Experience",
  "100+ Successful Projects Delivered",
  "24/7 Dedicated Support Team",
  "Agile Development Methodology",
  "Quality-First Development Standards",
  "Serving Clients Across India",
];

const stats = [
  { label: "Projects Done", value: "100+", color: "#f59e0b", },
  { label: "Team Members", value: "15+ Experts", color: "#0ea5e9", },
  { label: "States Served", value: "5+ States", color: "#10b981", },
  { label: "Client Rating", value: "4.8 / 5.0", color: "#8b5cf6", },
];

const values = [
  { icon: <FaHeart />, title: "Client First", desc: "Every decision we make is centered around delivering maximum value to our clients.", color: "#ef4444" },
  { icon: <FaShieldAlt />, title: "Transparency", desc: "Honest pricing, clear timelines, and open communication — always.", color: "#0ea5e9" },
  { icon: <FaLightbulb />, title: "Innovation", desc: "We stay ahead of the curve with the latest technologies and creative solutions.", color: "#f59e0b" },
  { icon: <FaHandshake />, title: "Long-term Partnership", desc: "We don't just deliver projects — we build lasting relationships.", color: "#10b981" },
];

const milestones = [
  { year: "2024", title: "Onexvora Founded", desc: "Started with a vision to help Indian businesses go digital.", icon: <FaRocket /> },
  { year: "2024", title: "First 100 Clients", desc: "Delivered web & mobile projects for clients across Delhi NCR.", icon: <FaUsers /> },
  { year: "2024", title: "100+ Projects", desc: "Expanded services to Digital Marketing & Social Media Management.", icon: <FaChartLine /> },
  { year: "2025", title: "5+ States", desc: "Now serving clients across UP, Delhi, Maharashtra & more.", icon: <FaGlobe /> },
];

const services = [
  { icon: <FaCode />, name: "Web Development", color: "#0ea5e9" },
  { icon: <FaMobile />, name: "Mobile Apps", color: "#8b5cf6" },
  { icon: <FaChartLine />, name: "Digital Marketing", color: "#ef4444" },
];

function AboutModal({ onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 30 }}
        transition={{ duration: 0.35, type: "spring", damping: 25 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl scrollbar-hide"
        style={{ background: "rgba(8,12,25,0.98)", border: "1px solid rgba(255,255,255,0.1)" }}
      >
        {/* Header */}
        <div className="relative h-40 sm:h-52 overflow-hidden rounded-t-3xl flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, rgba(14,165,233,0.3), rgba(139,92,246,0.3))" }}>
          <div className="absolute inset-0"
            style={{
              backgroundImage: "linear-gradient(rgba(14,165,233,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(14,165,233,0.15) 1px, transparent 1px)",
              backgroundSize: "30px 30px"
            }} />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[rgba(8,12,25,0.9)]" />

          <div className="relative z-10 text-center px-4">
            <motion.img
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: "spring", damping: 15 }}
              src="/circle logo.jpeg"
              alt="ONexvora"
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full mx-auto mb-3 border-2 border-blue-500/50 shadow-xl"
            />
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-white font-black text-2xl sm:text-3xl"
            >
              Onexvora Business Solutions
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-blue-400 text-xs sm:text-sm mt-1"
            >
              Your Digital Growth Partner — Noida, India
            </motion.p>
          </div>

          <button onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 glass rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors z-20">
            <FaTimes />
          </button>
        </div>

        <div className="p-5 sm:p-8 space-y-8">

          {/* Who We Are */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h3 className="text-white font-black text-lg sm:text-xl mb-3 flex items-center gap-2">
              <span className="w-1 h-6 rounded-full gradient-bg inline-block" />
              Who We Are
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-3">
              Onexvora Business Solutions is a fresh, passionate technology company based in Noida, Uttar Pradesh. We are dedicated to transforming businesses through innovative digital solutions — combining creativity with technical expertise to deliver results that exceed expectations.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed">
              Though we're in our first year, our team of skilled engineers, designers, and strategists bring sharp expertise and a startup mindset — fast delivery, honest pricing, and a genuine commitment to your growth. We believe every business deserves world-class digital solutions, regardless of size.
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + i * 0.08 }}
                  className="glass rounded-2xl p-4 text-center"
                >
                  <div className="text-xl mb-1" style={{ color: s.color }}>{s.icon}</div>
                  <div className="text-white font-black text-lg">{s.value}</div>
                  <div className="text-gray-400 text-xs">{s.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Our Values */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <h3 className="text-white font-black text-lg sm:text-xl mb-4 flex items-center gap-2">
              <span className="w-1 h-6 rounded-full gradient-bg inline-block" />
              Our Core Values
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {values.map((v, i) => (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.35 + i * 0.08 }}
                  className="glass rounded-2xl p-4 flex gap-3"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${v.color}20`, color: v.color }}>
                    {v.icon}
                  </div>
                  <div>
                    <div className="text-white font-bold text-sm mb-1">{v.title}</div>
                    <div className="text-gray-400 text-xs leading-relaxed">{v.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Journey / Timeline */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <h3 className="text-white font-black text-lg sm:text-xl mb-4 flex items-center gap-2">
              <span className="w-1 h-6 rounded-full gradient-bg inline-block" />
              Our Journey
            </h3>
            <div className="space-y-3">
              {milestones.map((m, i) => (
                <motion.div
                  key={m.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + i * 0.08 }}
                  className="flex gap-4 items-start"
                >
                  <div className="flex flex-col items-center">
                    <div className="w-9 h-9 rounded-xl gradient-bg flex items-center justify-center text-white text-sm flex-shrink-0">
                      {m.icon}
                    </div>
                    {i < milestones.length - 1 && <div className="w-0.5 h-6 bg-white/10 mt-1" />}
                  </div>
                  <div className="pb-2">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-blue-400 text-xs font-bold">{m.year}</span>
                      <span className="text-white font-bold text-sm">{m.title}</span>
                    </div>
                    <p className="text-gray-400 text-xs">{m.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* What We Do */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <h3 className="text-white font-black text-lg sm:text-xl mb-4 flex items-center gap-2">
              <span className="w-1 h-6 rounded-full gradient-bg inline-block" />
              What We Do
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {features.map((f, i) => (
                <motion.div
                  key={f}
                  initial={{ opacity: 0, x: 15 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.45 + i * 0.06 }}
                  className="flex items-center gap-2"
                >
                  <FaCheckCircle className="text-blue-400 flex-shrink-0 text-sm" />
                  <span className="text-gray-300 text-xs sm:text-sm">{f}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Location + Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="glass rounded-2xl p-5 flex flex-col sm:flex-row items-center gap-4"
            style={{ border: "1px solid rgba(14,165,233,0.2)" }}
          >
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2 text-gray-300 text-sm">
                <FaMapMarkerAlt className="text-blue-400" />
                Noida Sector 63, Uttar Pradesh, India
              </div>
              <div className="flex items-center gap-2 text-gray-300 text-sm">
                <FaEnvelope className="text-blue-400" />
                sahud9760@gmail.com
              </div>
            </div>
            <Link to="contact" smooth duration={600} offset={-80} onClick={onClose}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary px-6 py-3 rounded-full text-white font-semibold text-sm whitespace-nowrap"
              >
                <span>Get In Touch</span>
              </motion.button>
            </Link>
          </motion.div>

        </div>
      </motion.div>
    </motion.div>
  );
}

export default function About() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [showModal, setShowModal] = useState(false);

  return (
    <section id="about" className="py-16 sm:py-24 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-purple-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-blue-500/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        {/* Left Visual */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, x: -60 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="relative order-2 lg:order-1"
        >
          <div className="relative w-full max-w-sm sm:max-w-md mx-auto">
            <div className="absolute inset-0 gradient-bg rounded-3xl opacity-20 blur-2xl" />
            <div className="relative glass rounded-3xl p-5 sm:p-8">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-white/10">
                <img src="/circle logo.jpeg" alt="ONexvora" className="w-10 h-10 rounded-full object-contain" />
                <div>
                  <div className="text-white font-bold text-sm">Onexvora</div>
                  <div className="text-gray-400 text-xs">Business Solutions</div>
                </div>
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="ml-auto glass px-3 py-2 rounded-2xl shadow-xl"
                >
                  <div className="flex items-center gap-2">
                    <img src="/circle logo.jpeg" alt="ONexvora" className="w-6 h-6 rounded-full object-contain" />
                    <div>
                      <div className="text-white font-bold text-xs">2 Year</div>
                      <div className="text-gray-400 text-xs">of Excellence</div>
                    </div>
                  </div>
                </motion.div>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {stats.map((item, i) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                    className="glass rounded-2xl p-3 sm:p-4 flex flex-col items-center justify-center text-center card-hover"
                  >
                    <div className="text-xl sm:text-2xl mb-2" style={{ color: item.color }}>{item.icon}</div>
                    <div className="text-white font-bold text-base sm:text-lg">{item.value}</div>
                    <div className="text-gray-400 text-xs">{item.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right Content */}
        <motion.div
          initial={{ opacity: 0, x: 60 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="order-1 lg:order-2"
        >
          <span className="text-blue-400 font-semibold text-xs sm:text-sm uppercase tracking-widest">About Us</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mt-3 mb-5">
            We Are <span className="gradient-text">Onexvora</span>
            <br />Your Digital Partner
          </h2>
          <p className="text-gray-400 leading-relaxed mb-4 text-sm sm:text-base">
            Onexvora Business Solutions is a fresh, passionate technology company dedicated to transforming businesses through innovative digital solutions. We combine creativity with technical expertise to deliver results that exceed expectations.
          </p>
          <p className="text-gray-400 leading-relaxed mb-7 text-sm sm:text-base">
            Though we're new, our team of skilled engineers, designers, and strategists bring sharp expertise and a startup mindset — fast delivery, honest pricing, and a genuine commitment to your growth.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-8">
            {features.map((feature, i) => (
              <motion.div
                key={feature}
                initial={{ opacity: 0, x: 20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.4 + i * 0.08 }}
                className="flex items-center gap-2 sm:gap-3"
              >
                <FaCheckCircle className="text-blue-400 flex-shrink-0 text-sm" />
                <span className="text-gray-300 text-xs sm:text-sm">{feature}</span>
              </motion.div>
            ))}
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowModal(true)}
            className="btn-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full text-white font-semibold text-sm sm:text-base"
          >
            <span>Learn More About Us</span>
          </motion.button>
        </motion.div>
      </div>

      <AnimatePresence>
        {showModal && <AboutModal onClose={() => setShowModal(false)} />}
      </AnimatePresence>
    </section>
  );
}
